import { useEffect, useRef } from 'react';

/**
 * Closes a `<details>` popover on an outside click or Escape.
 *
 * `<details>`/`<summary>` already gives a compact toggle for free — click
 * (and Enter/Space on the trigger) opens and closes it with no JavaScript at
 * all, which matters for `LanguageSwitcher`: its links must stay reachable
 * before hydration finishes. This hook only adds the two behaviours the
 * element does not have natively: it does not close itself when the pointer
 * lands elsewhere on the page, and it does not treat Escape as "close".
 *
 * State lives on the DOM node (`element.open`), not in React — a listener
 * that reads `ref.current.open` at event time needs no dependency on open
 * state and never goes stale, so the same two listeners can stay mounted for
 * the component's whole lifetime instead of being torn down and rebuilt
 * every time the popover opens or closes.
 */
export function useDismissibleDetails<T extends HTMLDetailsElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    function close(returnFocus: boolean) {
      const node = ref.current;
      if (!node?.open) return;
      node.open = false;
      if (returnFocus) node.querySelector('summary')?.focus();
    }

    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) close(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close(true);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return ref;
}
